import {
    Connection as Connection_put,
    PublicKey as PublicKey_put,
    TokenAmount as TokenAmount_put
} from "@com.put/web3.js"
import { BigNumber } from "@ethersproject/bignumber"
import { WalletConnectModal } from "@walletconnect/modal"
import Client from "@walletconnect/sign-client"
import { SessionTypes } from "@walletconnect/types"
import { getSdkError } from "@walletconnect/utils"
import { Contract, ethers, formatUnits, isAddress, toQuantity } from "ethers"

import ERC20 from "@/abi/ERC20.json"
import { getRequiredNamespaces } from "@/config"
import { StoreOrder } from "@/store/payment"
import { getChainDetail } from "@/utils/utils"

import {
    getTrustWalletInjectedProvider,
    Tron,
    TronLink,
    TronWeb,
    useWeb3Provider
} from "./useContract"

interface SplTokenAmount extends TokenAmount_put {
    pubKey: PublicKey_put
}

async function getTokenAccountFromOwner(connection: Connection_put, publicKeys: PublicKey_put[]): Promise<SplTokenAmount | null> {
    let tokenAmount = null
    let tokenAmounts = await Promise.all(publicKeys.map(publicKey => connection.getTokenAccountBalance(publicKey)))

    for (let i = 0; i < tokenAmounts.length; ++i) {
        if (tokenAmount === null || +tokenAmounts[i].value.amount >= +tokenAmount?.amount) {
            tokenAmount = {
                ...tokenAmounts[i].value,
                pubKey: publicKeys[i]
            }
        }
    }

    return tokenAmount
}

class Connect {
    private order: StoreOrder
    private isMobile: boolean = /(iPhone|iPad|iPod|Opera Mini|Android.*Mobile|NetFront|PSP|BlackBerry|Windows Phone)/ig.test(window.navigator.userAgent)
    private setLoading: (boolean: boolean) => void
    message = {
        code: -1995,
        message: "Insufficient balance"
    }
    errorMessage: {
        code: -2000,
        message: "network error"
    }

    constructor(
        order: StoreOrder,
        setLoading: (boolean: boolean) => void
    ) {
        this.order = order
        this.setLoading = setLoading
    }

    private open(href: string) {
        let a = document.createElement("a")
        a.target = "_blank"
        a.href = href
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
    }

    private async EVMBalanceVerify(address: string, session?: SessionTypes.Struct): Promise<{
        address: string
        session?: SessionTypes.Struct
    }> {
        const JsonRpcProvider = new ethers.JsonRpcProvider(this.order.chain_info.rpc)

        return new Promise((resolve, reject) => {
            if (isAddress(this.order.contract)) {
                console.log("evm token balance verify")
                const contract = new Contract(
                    this.order.contract,
                    ERC20,
                    JsonRpcProvider
                )

                console.log("evm token contract balance of: ", address)
                contract.balanceOf(address).then((balance) => {
                    console.log("evm token balance---->", formatUnits(balance, this.order.decimal), this.order.amount)
                    if (+ethers.formatUnits(balance, this.order.decimal) >= +this.order.amount) {
                        resolve({ address, session })
                    }
                    else {
                        reject(this.message)
                    }
                }).catch(reject.bind(void 0, this.errorMessage))
            }
            else {
                console.log("evm coin verify: ", address)
                JsonRpcProvider.getBalance(address).then(balance => {
                    console.log(ethers.formatUnits(balance, this.order.decimal))
                    if (+ethers.formatUnits(balance, this.order.decimal) >= +this.order.amount) {
                        resolve({ address, session })
                    }
                    else {
                        reject(this.message)
                    }
                }).catch(reject.bind(void 0, this.errorMessage))
            }
        })
    }

    private async TronBalanceVerify({address, session, tronWeb}: {
        address: string, tronWeb: TronWeb | null, session?: SessionTypes.Struct,
    }): Promise<{
        address: string
        session?: SessionTypes.Struct
    }> {
        tronWeb?.setAddress(address)

        return new Promise(async (resolve, reject) => {
            if (this.order.contract === "TRX") {
                tronWeb?.trx.getBalance(address).then((balance: number) => {
                    console.log("tron trx balance: ", balance, balance / 1e6)
                    if (balance / 1e6 >= +this.order.amount) {
                        resolve({
                            address,
                            session
                        })
                    }
                    else {
                        reject(this.message)
                    }
                }).catch(reject.bind(void 0, this.errorMessage))
            }
            else {
                const contract = tronWeb?.contract(ERC20 as any, this.order.contract)

                contract.balanceOf(address).call().then((data: BigNumber) => {
                    const balance = parseInt(data._hex.toString(), 16) / 10 ** this.order.decimal
                    console.log("tron token balance: ", balance, +this.order.amount)

                    if (balance >= +this.order.amount) {
                        resolve({
                            address,
                            session
                        })
                    }
                    else {
                        reject(this.message)
                    }
                }).catch((err: any) => {
                    console.log("tron balance verify error: ", err)
                    reject.bind(void 0, this.errorMessage)
                })
            }
        })
    }

    async okx() {
        if (!window.okxwallet) {
            throw new Error("OKX wallet extension is not installed!")
        }

        // TODO: handle the tron chain 
        return new Promise(async (resolve, reject) => {
            try {
                this.setLoading(true)
                console.log("start connect okx wallet---->", this.order.chain)
    
                if (this.order.chain === "Tron") {
                    let tronLink = window.okxwallet?.tronLink
                    console.log("pre connect tron account----->")
    
                    if (tronLink) {
                        this.tronLink(undefined, tronLink as any).then(resolve).catch(reject)
                    }
                    return
                }
    
                // 检查是否已获得用户授权
                let accounts = await window.okxwallet.request({ method: "eth_accounts" });
                console.log("eth_accounts", accounts);
                
                if (!accounts || accounts.length === 0) {
                    console.log("start request accounts------>", window.okxwallet, "eth_requestAccounts")
                    const res = await window.okxwallet.request({ method: 'eth_requestAccounts' });
                    console.log("eth_requestAccounts", res)
                    accounts = res
                }
                
                const [address] = accounts
                
                if (!address) {
                    reject({ message: "Not found account" })
                    return
                }
                
                const chainId = await window.okxwallet.request({ method: 'eth_chainId' });
                if (Number(chainId) !== this.order.chain_id) {
                    const res = await window.okxwallet.request({ 
                        method: 'wallet_switchEthereumChain',
                        params: [{ chainId: `0x${this.order.chain_id.toString(16)}` }]
                    }).catch(() => window.okxwallet.request({ 
                        method: 'wallet_addEthereumChain',
                        params: [getChainDetail(this.order)]
                    }))
                    console.log(2222, res)
                }
    
                this.EVMBalanceVerify(address).then(resolve).catch(reject)
            } catch (err) {
                console.error(err)
                console.log(err.code, err.message)
                if (err.code === 4001 || err.code === "ACTION_REJECTED") {
                    reject({ message: err.message })
                    return
                }

                reject(err)
            }
        })
    }

    async metamask() {
        if (this.isMobile && !window.ethereum) {
            let url = ""
            switch (this.order.mode) {
                case "contract":
                case "mix":
                    url = "https://metamask.app.link/dapp/" + window.location.href
                    break
                case "address":
                    url = "https://metamask.app.link/send/"
                    if (ethers.isAddress(this.order.contract)) {
                        url += `${this.order.contract}@${this.order.chain_id}/transfer?address=${this.order.pay_address}&uint256=${ethers.parseUnits(this.order.amount, this.order.decimal).toString()}`
                    }
                    else {
                        url += `${this.order.pay_address}@${this.order.chain_id}?value=${toQuantity(ethers.parseEther(this.order.amount))}`
                    }
                    break
            }
            this.open(url)
            return Promise.reject({})
        }
        
        return new Promise(async (resolve, reject) => {
            try {
                this.setLoading(true)
                const { ethereumProvider, Web3Provider } = await useWeb3Provider()
                
                if (!ethereumProvider) {
                    throw new Error("Metamask extension is not installed!")
                }

                // 检查是否已获得用户授权
                let accounts = await ethereumProvider.request({ method: "eth_accounts" }) as string[];

                if (!accounts || accounts.length === 0) {
                    const res = await ethereumProvider.request({ method: 'eth_requestAccounts' }) as string[];
                    accounts = res
                }
                
                const [address] = accounts
                let network = await Web3Provider?.getNetwork()

                if (!address) {
                    reject({ message: "Not found account" })
                    return
                }

                if (Number(network?.chainId || 0) !== this.order.chain_id) {
                    try {
                        await ethereumProvider.request?.({
                            method: "wallet_switchEthereumChain",
                            params: [
                                {
                                    chainId: `0x${this.order.chain_id.toString(16)}`
                                }
                            ]
                        })
                    } catch (err) {
                        console.log("metamask wallet_switchEthereumChain error: ", err)
                        if (err.code === 4902) {
                            try {
                                await ethereumProvider?.request?.({
                                    method: "wallet_addEthereumChain",
                                    params: [getChainDetail(this.order)]
                                })
                            } catch {
                                return
                            }
                        }
                        else {
                            reject({
                                message: `doesn't match to network selected in wallet (network with id: ${this.order.chain_id})`
                            })
                            return
                        }
                    }
                }
                this.EVMBalanceVerify(address).then(resolve).catch(reject)
            } catch(err) {
                reject(err)
            }
        })
    }

    async tronLink(_?: any,  provider?: TronLink | null) {
        console.log("start tron link wallet------>", provider)
        const tron = Tron()
        const tronLink = provider ?? TronLink()
        
        if (tronLink) {
            this.setLoading(true)
            return new Promise(async (resolve, reject) => {
                try {
                    if (!tronLink.ready) {
                        console.log("connect tron account---->", "tron_requestAccounts")
                        let requestAccountsResponse = await tronLink.request<{code: any}>(
                            {
                                method: "tron_requestAccounts",
                                params: {
                                    websiteIcon: "<WEBSITE ICON URI>",
                                    websiteName: "<WEBSITE NAME>",
                                }
                            }
                        )
    
                        if (requestAccountsResponse.code !== 200) {
                            reject(requestAccountsResponse)
                            return
                        }
                    }
                    if (tron) {
                        try {
                            console.log("tron wallet switch ethereum chain")
                            if (this.isMobile) {
                                await tronLink.request({
                                    method: "wallet_switchEthereumChain",
                                    params: [{ chainId: `0x${this.order.chain_id.toString(16)}` }],
                                })
                            }
                            else {
                                await tron.request({
                                    method: "wallet_switchEthereumChain",
                                    params: [{ chainId: `0x${this.order.chain_id.toString(16)}` }],
                                })
                            }
                        } catch (err) {
                            console.log(err, "tr")
                            reject(err)
                            return
                        }
                    }
    
                    console.log("start tron balance verify")
                    const tronWeb: TronWeb | null =  await new Promise<Exclude<ReturnType<typeof TronLink>, null>>(_resolve => {
                        let tronWeb: ReturnType<typeof TronLink> = null,
                            timer: ReturnType<typeof setInterval> | null = null
                        timer = setInterval(() => {
                            tronWeb = tronLink.tronWeb || TronWeb()
                            if (tronWeb && timer) {
                                _resolve(tronWeb)
                                clearInterval(timer)
                            }
                        }, 100)
                    })
                    this.TronBalanceVerify({
                        address: tronWeb?.defaultAddress.base58,
                        tronWeb,
                    }).then(resolve).catch(reject)
                } catch(error) {
                    return Promise.reject(error)
                }
            })
        }
        
        else {
            console.log("open tron link")
            let tronURLParams = encodeURIComponent(JSON.stringify({
                "url": window.location.href,
                "action": "open",
                "protocol": "tronlink",
                "version": "1.0"
            }))
            this.open(`tronlinkoutside://pull.activity?param=${tronURLParams}`)
            return Promise.reject({})
        }
    }

    async broearnWallet(modal: {
        open(uri: string): void
        close(): void
    }) {
        const isBroearn = !!window.bw

        return new Promise(async (resolve, reject) => {
            const balanceVerify = async (address: string, session?: SessionTypes.Struct) => {
                console.log("broearn wallet balance verify: ", this.order.chain)
                switch (this.order.chain) {
                    case "PUT":
                        const { rpc } = this.order.chain_info
                        const connection = new Connection_put(rpc)
                        const pubKey = new PublicKey_put(address)
                        if (this.order.contract === "PUT") {
                            connection.getBalance(pubKey).then(balance => {
                                console.log(+balance.toString() / 10e8)
                                if (+balance.toString() / 10e8 >= +this.order.amount) {
                                    resolve({ address, session })
                                }
                                else {
                                    reject(this.message)
                                }
                            }).catch(reject.bind(void 0, this.errorMessage))
                        }
                        else {
                            const mintPublicKey = new PublicKey_put(this.order.contract)
                            try {
                                let baseTokenAccount = (await connection.getTokenAccountsByOwner(
                                    pubKey,
                                    {
                                        mint: mintPublicKey
                                    }
                                ))
                                let baseMintCurrency = await getTokenAccountFromOwner(connection, baseTokenAccount?.value.map(item => item.pubkey))
                                console.log(baseMintCurrency)
                                if (baseMintCurrency && baseMintCurrency.uiAmount && baseMintCurrency.uiAmount > +this.order.amount) {
                                    resolve({ address, session })
                                }
                                else {
                                    reject(this.message)
                                }
                            } catch {
                                reject(this.errorMessage)
                            }

                        }
                        break
                    case "Tron":
                        console.log("tron balance verify")
                        this.TronBalanceVerify({address, tronWeb: window.bw?.tron.tronWeb ?? null}).then(resolve).catch(reject)
                        break
                    default:
                        console.log("evm balance verify")
                        this.EVMBalanceVerify(address, session).then(resolve).catch(reject)
                        break
                }
            }

             if (isBroearn) {
                console.log("connect broearn")
                this.setLoading(true)
                let _session = localStorage.getItem("bw_session")
                const chainType = this.order.chain.toLowerCase();
                let provider = window.bw?.[!["put", "tron", "solana"].includes(chainType) ? "eth" : chainType];
                
                if (!provider) {
                    reject({ message: "Broearn wallet extension is not installed!" })
                    return
                }

                try {
                    const chainId  = await window.bw?.eth.request<number>({ method: 'eth_chainId' })
                    
                    if (Number(chainId) !== this.order.chain_id) {
                        await window.bw?.eth.request({
                            method: 'wallet_switchEthereumChain',
                            params: [{ chainId: this.order.chain_id }]
                        })
                    }

                    if (["put", "solana"].includes(chainType)) {
                        console.log(`broearn ${chainType} connect`)
                        provider.connect(_session ? { session: _session } : void 0).then(async ({ data: { address, session } }: any) => {
                            localStorage.setItem("bw_session", session)
                            await balanceVerify(address)
                        }).catch((err: any) => {
                            console.log(err, "err")
                            reject({
                                ...err,
                                message: err.errorMessage
                            })
                        }) as any
                        return
                    }
    
                    console.log("broearn request accounts: ");
                    let account: string | string[] = "";
                    if (chainType ===  "tron") {
                        const res = await provider.request({method: "tron_requestAccounts"});
                        account = res?.data.address;
                    } else {
                        account = await provider.request({ method: "eth_requestAccounts" }).catch((e: any) => {
                            console.error("broearn request account error", e);
                            reject({...e, message: "Request account failed, please retry after refresh page!"});
                        });
                    }
                    const address = Array.isArray(account) ? account[0] : account;
    
                    console.log(`broearn connect ${chainType}: `, address);
                    
                    if (!address) {
                        reject({ message: "Not found account" });
                        return;
                    }
    
                    balanceVerify(address);
                } catch(e) {
                    reject(e);
                    return;
                }
            }
            
            else {
                this.setLoading(true);
                console.time("Create connect time");
                let timeout = true,
                    then = true
                setTimeout(() => {
                    if (timeout) {
                        then = false
                        reject({
                            message: "Network timeout, Please try again"
                        })
                    }
                }, 15_000)
                const client = await Client.init({
                    relayUrl: "wss://relay.walletconnect.org",
                    projectId: "d79c84a4ed043335456a5bdf06ad7a53",
                    metadata: {
                        name: "ReelPay",
                        description: "Provides safe and convenient cryptocurrency payment solutions with the goal of financializing cryptocurrency.",
                        icons: ["https://reel-block-n.oss-ap-southeast-1.aliyuncs.com/base/202303/8ed0bf7c52c97a76.png"],
                        url: window.location.host
                    }
                })

                if (client.session.keys.length) {
                    await client.disconnect({
                        topic: client.session.get(client.session.keys[0]).topic,
                        reason: {
                            code: -200,
                            message: "disconnect"
                        }
                    }).catch(() => { })
                }
                let { uri, approval } = await client.connect({
                    requiredNamespaces: getRequiredNamespaces(this.order.chain, this.order.chain_id)
                })
                if (!then) return
                timeout = false
                console.timeEnd("Create connect time")
                this.setLoading(false)
                uri && modal.open(uri)

                approval().then(session => {
                    const [address] = Object.values(session.namespaces).map((namespace: any) => namespace.accounts.map((account: string) => {
                        let accounts = account.split(":")
                        return accounts[accounts.length - 1]
                    })).flat()
                    balanceVerify(address, session)
                    modal.close()
                }).catch((err) => {
                    modal.close()
                    reject(err.message)
                })
            }
        })
    }

    async walletconnect() {
        this.setLoading(true)
        let chain: string
        switch (this.order.chain) {
            case "PUT":
                chain = "put:"
                break
            case "Tron":
                chain = "tron:"
                break
            default:
                chain = "eip155:" + this.order.chain_id
        }

        return new Promise(async (resolve, reject) => {
            const balanceVerify = async (address: string, session?: SessionTypes.Struct) => {
                this.setLoading(true)
                switch (this.order.chain) {
                    case "PUT":
                        const connection = new Connection_put(this.order.chain_info.rpc)
                        const pubKey = new PublicKey_put(address)
                        if (this.order.contract === "PUT") {
                            connection.getBalance(pubKey).then(balance => {
                                console.log(+balance.toString() / 10e8)
                                if (+balance.toString() / 10e8 >= +this.order.amount) {
                                    resolve({
                                        address,
                                        session
                                    })
                                }
                                else {
                                    reject(this.message)
                                }
                            })
                        }
                        else {
                            const mintPublicKey = new PublicKey_put(this.order.contract)
                            let baseTokenAccount = (await connection.getTokenAccountsByOwner(
                                pubKey,
                                {
                                    mint: mintPublicKey
                                }
                            ))
                            let baseMintCurrency = await getTokenAccountFromOwner(connection, baseTokenAccount?.value.map(item => item.pubkey))
                            console.log(baseMintCurrency)
                            if (baseMintCurrency && baseMintCurrency.uiAmount && baseMintCurrency.uiAmount > +this.order.amount) {
                                resolve({ address, session })
                            }
                            else {
                                reject(this.message)
                            }
                        }
                        break
                    case "Tron":
                        this.TronBalanceVerify({
                            address,
                            tronWeb: window.tronWeb, 
                            session,
                        }).then(resolve).catch(reject)
                        break
                    default:
                        this.EVMBalanceVerify(address, session).then(resolve).catch(reject)
                        break
                }
            }
            console.time("Create connect time")
            let timeout = true,
                then = true
            setTimeout(() => {
                if (timeout) {
                    then = false
                    reject({
                        message: "Network timeout, Please try again"
                    })
                }
            }, 15_000)

            const client = await Client.init({
                relayUrl: "wss://relay.walletconnect.org",
                projectId: "d79c84a4ed043335456a5bdf06ad7a53",
                metadata: {
                    name: "ReelPay",
                    description: "Provides safe and convenient cryptocurrency payment solutions with the goal of financializing cryptocurrency.",
                    icons: ["https://reel-block-n.oss-ap-southeast-1.aliyuncs.com/base/202303/8ed0bf7c52c97a76.png"],
                    url: window.location.host
                }
            })

            if (client.session.keys.length) {
                try {
                    await client.disconnect({
                        topic: client.session.get(client.session.keys[0]).topic,
                        reason: getSdkError("USER_DISCONNECTED")
                    })
                } catch (err) {
                    console.log(err)
                }
            }

            let { uri, approval } = await client.connect({
                requiredNamespaces: getRequiredNamespaces(this.order.chain, this.order.chain_id)
            })
            if (!then) return
            timeout = false
            console.timeEnd("Create connect time")
            const modal = new WalletConnectModal({
                projectId: "d79c84a4ed043335456a5bdf06ad7a53",
                chains: [chain]
            })

            this.setLoading(false)
            modal.openModal({ uri })
            approval().then(session => {
                const [address] = Object.values(session.namespaces).map((namespace: any) => namespace.accounts.map((account: string) => {
                    let accounts = account.split(":")
                    return accounts[accounts.length - 1]
                })).flat()
                balanceVerify(address, session)
                modal.closeModal()
            }).catch((err) => {
                modal.closeModal()
                reject(err.message)
            })
        })
    }

    async trust() {
        return new Promise(async (resolve, reject) => {
            try {
                this.setLoading(true)
                const trustWallet: any = await getTrustWalletInjectedProvider()
                
                if (!trustWallet) {
                    throw new Error("Trust wallet extension is not installed!")
                }
                
                // 检查是否已获得用户授权
                let accounts = await trustWallet.request({ method: "eth_accounts" }) as string[];

                if (!accounts || accounts.length === 0) {
                    const res = await trustWallet.request({ method: 'eth_requestAccounts' }) as string[];
                    accounts = res
                }
                
                const [address] = accounts

                if (!address) {
                    reject({ message: "Not found account"})
                }

                const chainId = await trustWallet.request({ method: 'eth_chainId' });
            
                console.log(chainId, `0x${this.order.chain_id.toString(16)}`)

                if (chainId !== `0x${this.order.chain_id.toString(16)}`) {
                    await trustWallet.request({ 
                        method: 'wallet_switchEthereumChain',
                        params: [{ chainId: `0x${this.order.chain_id.toString(16)}` }]
                    })
                }
    
                this.EVMBalanceVerify(address).then(resolve).catch(reject)
            } catch (e) {
                if (e.code === 4001) {
                    console.error("User denied connection.");
                    reject({ message: "User denied connection." })
                    return
                }

                console.log("trust wallet connect error: ", e)
                reject(e)
            }
        })
    }
}

export { Connect }
export default Connect
